import {
    splitAmaronFlatDispositionIntoLevels,
    type AmaronDispositionLevels,
} from "~/domainModels/workspaceSpecific/amaron";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import type {AmaronPriorityLevel} from "~/utilities/workspaceSpecific/amaronPriorityMetric";
import {resolveAmaronLeadPriorityFromJourney} from "~/utilities/workspaceSpecific/amaronPriority";

export type AmaronLeadPriority = AmaronPriorityLevel;

/** How Task TAT computes `dueAt` (see `computeAmaronTaskTat.ts`). */
export type AmaronTaskTatDueRule =
    | {type: "offset-minutes"; minutes: number}
    | {type: "eod-calendar-today"}
    | {type: "eod-business-today"}
    | {type: "eod-business-next-day"}
    | {type: "eod-next-working-day"};

export type AmaronDispositionQueueBucket =
    | "new-lead"
    | "interested-follow-up"
    | "interested-need-more-info"
    | "interested-rebook"
    | "interested-slot-not-available"
    | "interested-op-hold"
    | "cbl1"
    | "cbl2"
    | "cbl3"
    | "nr1"
    | "nr2"
    | "nr3"
    | "nr4"
    | "nr5"
    | "nr6"
    | "call-hangup"
    | "booked"
    | "not-interested"
    | "unserviceable"
    | "terminal"
    | "generic-call";

export type AmaronDispositionQueueActionResult = {
    actionLabel: string;
    queueRank: number;
    queueBucket: AmaronDispositionQueueBucket;
    /** Omitted for terminal rows (`No action`). Timing via `computeAmaronTaskTat.ts` → `resolveAmaronDueAt`. */
    tatDueRule?: AmaronTaskTatDueRule;
};

const PRIORITY_OFFSET: Record<AmaronLeadPriority, number> = {
    P0: 0,
    P1: 1,
    P2: 2,
    P3: 3,
};

/** Maps any non-negative journey queue score → P0 | P1 | P2 | P3. */
export function resolveAmaronLeadPriority(
    journey: EventContainerWithEvents,
    eventContainerTemplate: EventContainerTemplate,
): AmaronLeadPriority {
    return resolveAmaronLeadPriorityFromJourney(journey, eventContainerTemplate);
}

const OP_HOLD_EXACT_SLUGS = new Set(["op-hold", "ops-hold", "operational-hold"]);

function normalizeOpHoldDispositionSlug(raw: string): string {
    return raw.trim().toLowerCase().replace(/_/g, "-");
}

/** Matches Op. Hold L4 variants after trim, lower-case, and `_` → `-` normalization. */
export function isOpHoldDispositionSlug(l4: string): boolean {
    const normalized = normalizeOpHoldDispositionSlug(l4);
    if (normalized === "") {
        return false;
    }
    if (OP_HOLD_EXACT_SLUGS.has(normalized)) {
        return true;
    }
    return (
        normalized.includes("op-hold")
        || normalized.includes("ops-hold")
        || (normalized.includes("operational") && normalized.includes("hold"))
    );
}

export function getAmaronDispositionQueueBucket(flatDisposition: string): AmaronDispositionQueueBucket {
    return classifyDispositionQueueBucket(splitAmaronFlatDispositionIntoLevels(flatDisposition));
}

function classifyDispositionQueueBucket(levels: AmaronDispositionLevels): AmaronDispositionQueueBucket {
    const l1 = levels.l1.trim().toLowerCase();
    const l2 = levels.l2.trim().toLowerCase();
    const l3 = levels.l3.trim().toLowerCase();
    const l4 = levels.l4.trim().toLowerCase();

    if (l1 === "new-lead") {
        return "new-lead";
    }
    if (l1 === "wrong-number") {
        return "terminal";
    }
    if (l3 === "interested" && l4 === "booked") {
        return "booked";
    }
    if (l3 === "interested" && l4 === "follow-up") {
        return "interested-follow-up";
    }
    if (l3 === "interested" && (l4 === "need-more-information" || l4 === "need-more-info")) {
        return "interested-need-more-info";
    }
    if (l3 === "interested" && isOpHoldDispositionSlug(l4)) {
        return "interested-op-hold";
    }
    if (l3 === "interested" && l4 === "slot-not-available") {
        return "interested-slot-not-available";
    }
    if (l3 === "interested" && l4 === "other") {
        return "interested-rebook";
    }
    if (l3 === "interested") {
        return "interested-follow-up";
    }
    if (l2 === "call-hung-up" || l2 === "call-hang-up") {
        return "call-hangup";
    }
    if (l3 === "cbl3") {
        return "cbl3";
    }
    if (l3 === "cbl2") {
        return "cbl2";
    }
    if (l3 === "cbl1" || l2 === "call-back-later") {
        return "cbl1";
    }
    if (l2 === "nr1") {
        return "nr1";
    }
    if (l2 === "nr2") {
        return "nr2";
    }
    if (l2 === "nr3") {
        return "nr3";
    }
    if (l2 === "nr4") {
        return "nr4";
    }
    if (l2 === "nr5") {
        return "nr5";
    }
    if (l2 === "nr6") {
        return "nr6";
    }
    if (l3 === "not-interested") {
        return "not-interested";
    }
    if (l3 === "unserviceable") {
        return "unserviceable";
    }
    if (l2 === "invalid" || l4 === "did-not-fill" || l4 === "filled-by-mistake") {
        return "terminal";
    }
    return "generic-call";
}

function resolvePriorityInterestedTatDueRule(priority: AmaronLeadPriority): AmaronTaskTatDueRule {
    if (priority === "P0") {
        return {type: "offset-minutes", minutes: 360};
    }
    if (priority === "P1") {
        return {type: "eod-business-today"};
    }
    return {type: "eod-business-next-day"};
}

function queueActionWithInterestedPriority(args: {
    actionLabel: string;
    priority: AmaronLeadPriority;
    queueRank: number;
    queueBucket: AmaronDispositionQueueBucket;
}): AmaronDispositionQueueActionResult {
    return {
        actionLabel: args.actionLabel,
        queueRank: args.queueRank,
        queueBucket: args.queueBucket,
        tatDueRule: resolvePriorityInterestedTatDueRule(args.priority),
    };
}

/** Agent queue rank (1 = highest). Matches priority grid / agent queue spreadsheet. */
export function resolveAmaronQueueRank(flatDisposition: string, priority: AmaronLeadPriority): number {
    const levels = splitAmaronFlatDispositionIntoLevels(flatDisposition);
    const bucket = classifyDispositionQueueBucket(levels);
    const offset = PRIORITY_OFFSET[priority];

    switch (bucket) {
        case "new-lead":
            return 1 + offset;
        case "interested-follow-up":
            return 4 + offset;
        case "interested-need-more-info":
            return 7 + offset;
        case "interested-rebook":
        case "interested-slot-not-available":
        case "interested-op-hold":
            return 10 + offset;
        case "cbl1":
        case "cbl2":
        case "cbl3":
            // Agent queue sheet ranks 13–15: CBL × P0/P1/P2 (same rank for CBL1–3 at a given priority).
            return 13 + offset;
        case "nr1":
            // Sheet ranks 16/19/22 (P0/P1/P2), then NR2 +1, NR3 +2 within each priority band.
            return 16 + offset * 3;
        case "nr2":
            return 17 + offset * 3;
        case "nr3":
            return 18 + offset * 3;
        case "nr4":
        case "nr5":
        case "nr6":
            // Sheet ranks 25–27: NR4/5/6 share rank per lead priority.
            return 25 + offset;
        case "call-hangup":
            return 28 + offset;
        case "booked":
            return 31;
        case "not-interested":
            return 32;
        case "unserviceable":
            return 33;
        case "terminal":
            return 34;
        case "generic-call":
            return 3;
    }
}

export function resolveAmaronDispositionQueueAction(
    flatDisposition: string,
    priority: AmaronLeadPriority,
): AmaronDispositionQueueActionResult {
    const levels = splitAmaronFlatDispositionIntoLevels(flatDisposition);
    const bucket = classifyDispositionQueueBucket(levels);
    const queueRank = resolveAmaronQueueRank(flatDisposition, priority);

    switch (bucket) {
        case "new-lead": {
            if (priority === "P0") {
                return {
                    actionLabel: "Call",
                    queueRank,
                    queueBucket: bucket,
                    tatDueRule: {type: "offset-minutes", minutes: 15},
                };
            }
            if (priority === "P1") {
                return {
                    actionLabel: "Call",
                    queueRank,
                    queueBucket: bucket,
                    tatDueRule: {type: "offset-minutes", minutes: 60},
                };
            }
            return {
                actionLabel: "Call",
                queueRank,
                queueBucket: bucket,
                tatDueRule: {type: "offset-minutes", minutes: 360},
            };
        }
        case "interested-follow-up": {
            return queueActionWithInterestedPriority({
                actionLabel: "Call",
                priority,
                queueRank,
                queueBucket: bucket,
            });
        }
        case "interested-need-more-info": {
            return queueActionWithInterestedPriority({
                actionLabel: "Call + senior agent flag",
                priority,
                queueRank,
                queueBucket: bucket,
            });
        }
        case "interested-rebook":
        case "interested-slot-not-available": {
            return queueActionWithInterestedPriority({
                actionLabel: "Call to re-book",
                priority,
                queueRank,
                queueBucket: bucket,
            });
        }
        case "interested-op-hold": {
            return queueActionWithInterestedPriority({
                actionLabel: "Op. Hold — call to re-book",
                priority,
                queueRank,
                queueBucket: bucket,
            });
        }
        case "cbl1":
        case "cbl2":
        case "cbl3": {
            return {
                actionLabel: "Call at slot",
                queueRank,
                queueBucket: bucket,
                // When scheduled call date + time are set, due is derived from the slot; otherwise calendar EOD.
                tatDueRule: {type: "eod-calendar-today"},
            };
        }
        case "nr1": {
            return {
                actionLabel: "Retry call",
                queueRank,
                queueBucket: bucket,
                tatDueRule: {type: "offset-minutes", minutes: 120},
            };
        }
        case "nr2": {
            return {
                actionLabel: "Retry call",
                queueRank,
                queueBucket: bucket,
                tatDueRule: {type: "offset-minutes", minutes: 240},
            };
        }
        case "nr3": {
            return {
                actionLabel: "Retry call",
                queueRank,
                queueBucket: bucket,
                // Priority sheet indicates 6h; keeping 360min until business confirms otherwise.
                tatDueRule: {type: "offset-minutes", minutes: 360},
            };
        }
        case "nr4":
        case "nr5":
        case "nr6": {
            return {
                actionLabel: "Retry call",
                queueRank,
                queueBucket: bucket,
                tatDueRule: {type: "eod-next-working-day"},
            };
        }
        case "call-hangup": {
            return {
                actionLabel: "WA message → then call",
                queueRank,
                queueBucket: bucket,
                tatDueRule: {type: "offset-minutes", minutes: 60},
            };
        }
        case "booked":
        case "not-interested":
        case "unserviceable":
        case "terminal": {
            return {actionLabel: "No action", queueRank, queueBucket: bucket};
        }
        case "generic-call": {
            return queueActionWithInterestedPriority({
                actionLabel: "Call",
                priority,
                queueRank,
                queueBucket: bucket,
            });
        }
    }
}

/** Dispositions that must not show Due by / Delayed by on disposition tracker cards. */
export const AMARON_TERMINAL_TASK_TAT_QUEUE_BUCKETS: ReadonlySet<AmaronDispositionQueueBucket> = new Set([
    "booked",
    "not-interested",
    "unserviceable",
    "terminal",
]);

export function isAmaronTerminalDispositionQueueBucket(bucket: AmaronDispositionQueueBucket): boolean {
    return AMARON_TERMINAL_TASK_TAT_QUEUE_BUCKETS.has(bucket);
}

export function sortAmaronDispositionKanbanJourneysByQueuePriority<
    T extends {amaronQueueRank: number; createdAt: string},
>(rows: Array<T>): Array<T> {
    return [...rows].sort((left, right) => {
        if (left.amaronQueueRank !== right.amaronQueueRank) {
            return left.amaronQueueRank - right.amaronQueueRank;
        }
        return right.createdAt.localeCompare(left.createdAt);
    });
}
